// src/api/aiToolsApi.js
import axios from 'axios';

// Define specific error codes for AI Tools API interactions
export const AI_TOOLS_ERROR_CODES = {
    NETWORK_ERROR: 'AI_NETWORK_ERROR',
    REQUEST_ERROR: 'AI_REQUEST_ERROR',
    CHAT_NOT_FOUND: 'CHAT_NOT_FOUND', // Match backend codes
    NOT_CHAT_OWNER: 'NOT_CHAT_OWNER',
    LLM_CONFIG_NOT_FOUND: 'LLM_CONFIG_NOT_FOUND',
    USER_SETTINGS_NOT_FOUND: 'USER_SETTINGS_NOT_FOUND',
    CONTEXT_LIMIT_EXCEEDED: 'CONTEXT_LIMIT_EXCEEDED',
    LLM_API_ERROR: 'LLM_API_ERROR',
    TOKEN_CALCULATION_ERROR: 'TOKEN_CALCULATION_ERROR',
    INVALID_INPUT: 'INVALID_INPUT', // Match backend
    DATABASE_ERROR: 'DATABASE_ERROR', // Match backend
    INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR', // Match backend
    UNAUTHORIZED: 'UNAUTHORIZED', // Match backend
    UNKNOWN_API_ERROR: 'UNKNOWN_AI_API_ERROR',
};

const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const aiApiUrl = `${baseUrl}/api/ai`; // Base URL for AI tools endpoints

// --- Helper to format errors consistently ---
const formatError = (error, defaultCode = AI_TOOLS_ERROR_CODES.UNKNOWN_API_ERROR) => {
    console.error('[aiToolsApi] Error:', error);
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const backendError = error.response.data;
        return {
            error: backendError?.error || `HTTP_ERROR_${error.response.status}`,
            message: backendError?.message || error.message || 'An API error occurred.',
            status: error.response.status,
            data: backendError?.data,
        };
    } else if (error.request) {
        // The request was made but no response was received
        return {
            error: AI_TOOLS_ERROR_CODES.NETWORK_ERROR,
            message: 'No response from server. Check network connection or server status.',
            status: null, // No HTTP status available
        };
    } else {
        // Something happened in setting up the request that triggered an Error
        return {
            error: error.code || AI_TOOLS_ERROR_CODES.REQUEST_ERROR, // Use error code if available (e.g., from APIError)
            message: error.message || 'Error setting up API request.',
            status: error.status_code || null, // Use status if available (e.g., from APIError)
        };
    }
};


// 1) Fetch LLM Options
export const fetchLLMOptions = async (token) => {
    const url = `${aiApiUrl}/llm-options`;
    console.debug('[fetchLLMOptions] Calling:', url);
    try {
        const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status >= 200 && status < 500, // Handle common errors gracefully
        });
        console.debug('[fetchLLMOptions] Response Status:', response.status);
        if (response.status === 200) {
            return response.data; // Expected: { options: [...], defaultId: ... }
        } else {
             console.warn('[fetchLLMOptions] Non-200 Response:', response.data);
            return formatError({ response }, AI_TOOLS_ERROR_CODES.UNKNOWN_API_ERROR);
        }
    } catch (error) {
        return formatError(error);
    }
};

// 2) Fetch Chat List
export const fetchChats = async (token) => {
    const url = `${aiApiUrl}/fetchChats`;
     console.debug('[fetchChats] Calling:', url);
    try {
        const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status >= 200 && status < 500,
        });
         console.debug('[fetchChats] Response Status:', response.status);
        if (response.status === 200) {
            return response.data; // Expected: [{ chatId, title, updatedAt }, ...]
        } else {
             console.warn('[fetchChats] Non-200 Response:', response.data);
            return formatError({ response }, AI_TOOLS_ERROR_CODES.UNKNOWN_API_ERROR);
        }
    } catch (error) {
        return formatError(error);
    }
};

// 3) Fetch Chat History
export const fetchChatHistory = async (token, chatId) => {
    if (!chatId) return { error: AI_TOOLS_ERROR_CODES.INVALID_INPUT, message: "Chat ID is required." };
    const url = `${aiApiUrl}/fetchChatHistory/${chatId}`;
     console.debug('[fetchChatHistory] Calling:', url);
    try {
        const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status >= 200 && status < 500,
        });
         console.debug('[fetchChatHistory] Response Status:', response.status);
        if (response.status === 200) {
            // Ensure the expected fields are present
            if (response.data && Array.isArray(response.data.messages) &&
                typeof response.data.currentTokenCount === 'number' &&
                typeof response.data.tokenLimit === 'number') {
                 return response.data;
            } else {
                console.warn('[fetchChatHistory] Unexpected response structure:', response.data);
                return { error: AI_TOOLS_ERROR_CODES.UNKNOWN_API_ERROR, message: "Received unexpected data structure for chat history." };
            }
        } else {
             console.warn('[fetchChatHistory] Non-200 Response:', response.data);
            return formatError({ response }, AI_TOOLS_ERROR_CODES.UNKNOWN_API_ERROR);
        }
    } catch (error) {
        return formatError(error);
    }
};

// 4) Create New Chat
export const createChat = async (token, params) => {
    // params expected: { prompt, llmConfigId, selectedInfluencers, knowledgeBasic, ... }
    const url = `${aiApiUrl}/createChat`;
     console.debug('[createChat] Calling:', url, 'with params:', params);
    try {
        const response = await axios.post(url, params, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            validateStatus: (status) => status >= 200 && status < 500,
        });
         console.debug('[createChat] Response Status:', response.status);
        if (response.status === 201) {
            return response.data; // Expected: { chatId, title, assistantMessage }
        } else {
             console.warn('[createChat] Non-201 Response:', response.data);
            return formatError({ response }, AI_TOOLS_ERROR_CODES.UNKNOWN_API_ERROR);
        }
    } catch (error) {
        return formatError(error);
    }
};

// 5) Send Message to Existing Chat
export const sendMessage = async (token, chatId, prompt) => {
     if (!chatId || !prompt) return { error: AI_TOOLS_ERROR_CODES.INVALID_INPUT, message: "Chat ID and prompt are required." };
    const url = `${aiApiUrl}/chats/${chatId}/messages`;
     console.debug('[sendMessage] Calling:', url, 'with prompt:', prompt);
    try {
        const response = await axios.post(url, { prompt }, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            validateStatus: (status) => status >= 200 && status < 500,
        });
         console.debug('[sendMessage] Response Status:', response.status);
        if (response.status === 200) {
            return response.data; // Expected: { assistantMessage }
        } else {
             console.warn('[sendMessage] Non-200 Response:', response.data);
            // Specifically handle context limit error if backend returns it
            if (response.data?.error === AI_TOOLS_ERROR_CODES.CONTEXT_LIMIT_EXCEEDED) {
                 return {
                    error: AI_TOOLS_ERROR_CODES.CONTEXT_LIMIT_EXCEEDED,
                    message: response.data.message || "Context limit exceeded.",
                    status: response.status,
                 }
            }
            return formatError({ response }, AI_TOOLS_ERROR_CODES.UNKNOWN_API_ERROR);
        }
    } catch (error) {
        return formatError(error);
    }
};

// 6) Set Custom Chat Title
export const setChatTitle = async (token, chatId, title) => {
     if (!chatId || !title) return { error: AI_TOOLS_ERROR_CODES.INVALID_INPUT, message: "Chat ID and title are required." };
    const url = `${aiApiUrl}/chats/${chatId}/title`;
     console.debug('[setChatTitle] Calling:', url, 'with title:', title);
    try {
        const response = await axios.put(url, { title }, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            validateStatus: (status) => status >= 200 && status < 500,
        });
         console.debug('[setChatTitle] Response Status:', response.status);
        if (response.status === 200) {
            return response.data; // Expected: { message: "..." }
        } else {
             console.warn('[setChatTitle] Non-200 Response:', response.data);
            return formatError({ response }, AI_TOOLS_ERROR_CODES.UNKNOWN_API_ERROR);
        }
    } catch (error) {
        return formatError(error);
    }
};

// 7) Delete Chat
export const deleteChat = async (token, chatId) => {
     if (!chatId) return { error: AI_TOOLS_ERROR_CODES.INVALID_INPUT, message: "Chat ID is required." };
    const url = `${aiApiUrl}/chats/${chatId}`;
     console.debug('[deleteChat] Calling:', url);
    try {
        const response = await axios.delete(url, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status >= 200 && status < 500,
        });
         console.debug('[deleteChat] Response Status:', response.status);
        if (response.status === 204) {
            return { success: true }; // Indicate success
        } else {
             console.warn('[deleteChat] Non-204 Response:', response.data);
            return formatError({ response }, AI_TOOLS_ERROR_CODES.UNKNOWN_API_ERROR);
        }
    } catch (error) {
        return formatError(error);
    }
};